import { IEmploymentDetailsPayload, IOtherIncomeDetailsPayload } from '@services/employmentAndIncomeDetails';
import { isEmpty, validate } from '@shared/hooks/useForm';
import { IEmploymentForm, IIncomeForm } from 'types/form';
import { monthsBetween } from 'utils/dateUtils';
import i18next from 'utils/i18n';

export const currentDate = new Date();
export const OTHER_EMPLOYMENT = 'Other';
export const EMPLOYMENT_TYPES = ['FullTime', 'PartTime', 'Contract'];
export const NON_EMPLOYMENT_TYPES = ['Self Employed', 'Student', 'Unemployed'];

export const employmentFormValidation = {
  incomeType: validate.string('incomeType').required(i18next.t('employmentAndIncome.incomeTypeValidation')),
  employersName: validate
    .string('employersName')
    .required(i18next.t('employmentAndIncome.employmentForm.validation.employersName')),
  jobTitle: validate.string('jobTitle').required(i18next.t('employmentAndIncome.employmentForm.validation.jobTitle')),
  employersAddress: validate
    .string('employersAddress')
    .required(i18next.t('employmentAndIncome.employmentForm.validation.employersAddress')),
  employersAPTSuiteNumber: validate.string('employersAPTSuiteNumber'),
  employersZipCode: validate
    .string('employersZipCode')
    .required(i18next.t('employmentAndIncome.employmentForm.validation.employersZipCode.required'))
    .matches(/^\d{5}$/, i18next.t('employmentAndIncome.employmentForm.validation.employersZipCode.validZipcode')),
  employersCity: validate
    .string('employersCity')
    .required(i18next.t('employmentAndIncome.employmentForm.validation.employersCity')),
  employersState: validate
    .string('employersState')
    .required(i18next.t('employmentAndIncome.employmentForm.validation.employersState')),
  employersPhoneNumber: validate
    .string('employersPhoneNumber')
    .required(i18next.t('employmentAndIncome.employmentForm.validation.employersPhoneNumber.required'))
    .matches(/^\d{10}$/, i18next.t('employmentAndIncome.employmentForm.validation.employersPhoneNumber.validPhoneNumber')),

  startDate: validate.string('startDate').required(i18next.t('employmentAndIncome.employmentForm.validation.startDate')),
  isCurrentIncomeSource: validate.boolean('isCurrentIncomeSource'),
  endDate: validate.string('endDate').custom((value, formValues) => {
    const isCurrent = formValues?.isCurrentIncomeSource;
    // Require endDate if it is not current employment
    if (!isCurrent && isEmpty(value)) return false;
    return true;
  }, i18next.t('employmentAndIncome.employmentForm.validation.endDate')),
  annualIncome: validate.number('annualIncome'),
};

export const incomeFormValidation = {
  incomeType: validate.string('incomeType').required(i18next.t('employmentAndIncome.incomeTypeValidation')),
  otherIncomeSource: validate
    .string('otherIncomeSource')
    .required(i18next.t('employmentAndIncome.incomeForm.validation.otherIncomeSource')),
  startDate: validate.string('startDate').required(i18next.t('employmentAndIncome.incomeForm.validation.startDate')),
  isCurrentIncomeSource: validate.boolean('isCurrentIncomeSource'),
  endDate: validate.string('endDate').custom((value, formValues) => {
    const isCurrent = formValues?.isCurrentIncomeSource;
    // Require endDate if it is not current income
    if (!isCurrent && isEmpty(value)) return false;
    return true;
  }, i18next.t('employmentAndIncome.incomeForm.validation.endDate')),
  monthlyAmount: validate.number('monthlyAmount'),
};

export const nonEmploymentValidation = {
  incomeType: validate.string('incomeType').required(i18next.t('employmentAndIncome.incomeTypeValidation')),
};

export const formatEmploymentDetails = (employmentDetails: IEmploymentForm): IEmploymentDetailsPayload => {
  const startDate = new Date(employmentDetails.startDate);
  let monthsDifference = 0;

  if (employmentDetails.isCurrentIncomeSource) {
    monthsDifference = monthsBetween(startDate, currentDate);
  } else if (employmentDetails.endDate) {
    monthsDifference = monthsBetween(startDate, employmentDetails.endDate);
  }

  return {
    monthsAtJob: monthsDifference,
    jobTitle: employmentDetails.jobTitle,
    employmentType: employmentDetails.incomeType,
    companyName: employmentDetails.employersName,
    monthlyIncomeAmount:
      employmentDetails.annualIncome === undefined || employmentDetails.annualIncome === 0
        ? 0
        : employmentDetails.annualIncome / 12,
    isCurrent: employmentDetails.isCurrentIncomeSource,
    isPrimary: employmentDetails.isCurrentIncomeSource,
    streetLine1: employmentDetails.employersAddress,
    streetLine2: employmentDetails.employersAPTSuiteNumber,
    city: employmentDetails.employersCity,
    state: employmentDetails.employersState,
    zip: employmentDetails.employersZipCode,
    phone: employmentDetails.employersPhoneNumber,
    startDate: employmentDetails.startDate,
    endDate: employmentDetails.endDate ?? '',
  };
};

export const formatOtherIncomeDetails = (otherIncomeDetails: IIncomeForm): IOtherIncomeDetailsPayload => {
  const startDate = new Date(otherIncomeDetails.startDate);
  let monthsDifference = 0;

  if (otherIncomeDetails.isCurrentIncomeSource) {
    monthsDifference = monthsBetween(startDate, currentDate);
  } else if (otherIncomeDetails.endDate) {
    monthsDifference = monthsBetween(startDate, otherIncomeDetails.endDate);
  }
  return {
    monthsAtJob: monthsDifference,
    incomeType: otherIncomeDetails.otherIncomeSource,
    isCurrent: otherIncomeDetails.isCurrentIncomeSource,
    monthlyAmount: otherIncomeDetails.monthlyAmount,
    startDate: otherIncomeDetails.startDate,
    endDate: otherIncomeDetails.endDate ?? '',
  };
};

export const addOrEditEmployment = (employment: IEmploymentDetailsPayload): IEmploymentForm => {
  return {
    incomeType: employment.employmentType,
    employersName: employment.companyName,
    jobTitle: employment.jobTitle,
    employersAddress: employment.streetLine1,
    employersAPTSuiteNumber: employment.streetLine2,
    employersZipCode: employment.zip,
    employersCity: employment.city,
    employersState: employment.state,
    employersPhoneNumber: employment.phone,
    startDate: employment.startDate,
    isCurrentIncomeSource: employment.isCurrent,
    annualIncome: employment.monthlyIncomeAmount * 12,
    endDate: employment.endDate,
  };
};

export const addOrEditOtherIncome = (otherIncome: IOtherIncomeDetailsPayload): IIncomeForm => {
  return {
    incomeType: OTHER_EMPLOYMENT,
    otherIncomeSource: otherIncome.incomeType,
    startDate: otherIncome.startDate ?? '',
    isCurrentIncomeSource: otherIncome.isCurrent,
    monthlyAmount: otherIncome.monthlyAmount,
    endDate: otherIncome.endDate ?? '',
  };
};
